import React, { useCallback, useEffect } from 'react';

import { Journeys, Targets, useCrossFlow } from '@atlassiansox/cross-flow-support';
import { cssMap } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { Anchor, Box, Stack } from '@atlaskit/primitives/compiled';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { getATLContextDomain } from '@atlassian/atl-context';

import { useFireAnalyticEvent } from '../analytics';
import { UFOSegmentNames } from '../common/constants';
import { EmptyState } from '../common/empty-state/EmptyState';
import { ProjectPickerLoadingSkeleton } from '../common/loading-skeletons/ProjectPickerLoadingSkeleton';
import { useOrgAdminPermission } from '../services/useOrgAdminPermission';
import type { Product } from '../types';

import { messages } from './messages';

type ProjectsAccessProps = {
	accountId?: string;
	orgId?: string;
	cloudId?: string;
	product?: Product;
	requestAccessSimplifiedUI?: boolean;
};

const styles = cssMap({
	container: {
		width: '300px',
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
	},
});

const getTownsquareUrl = ({ orgId, cloudId }: { orgId: string; cloudId: string }) =>
	`https://${getATLContextDomain('admin')}/o/${orgId}/townsquare?cloudId=${cloudId}&isSignup=true`;

export const ProjectsAccess = ({
	accountId,
	cloudId,
	orgId,
	product,
	requestAccessSimplifiedUI = true,
}: ProjectsAccessProps): React.JSX.Element => {
	const crossFlow = useCrossFlow();
	const { formatMessage } = useIntl();
	const { isOrgAdmin, loading, hasErrorOccurred } = useOrgAdminPermission(orgId, accountId);
	const { fireAnalyticsEvent } = useFireAnalyticEvent();

	useEffect(() => {
		fireAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'townsquareProjectPicker',
			eventType: 'ui',
			analyticsAttributes: {
				screen: 'PROJECTS_ACCESS',
				isOrgAdmin,
				isProjectFieldEmpty: requestAccessSimplifiedUI,
			},
		});
	}, [fireAnalyticsEvent, isOrgAdmin, requestAccessSimplifiedUI]);

	const onRequestAccess = useCallback(async () => {
		if (crossFlow.isEnabled) {
			await crossFlow.api.open({
				journey: Journeys.GET_STARTED,
				sourceComponent: 'project-picker',
				sourceContext: product ?? 'jira',
				targetProduct: Targets.ATLAS,
			});
		}
	}, [crossFlow, product]);

	if (hasErrorOccurred) {
		throw new Error('Error checking org admin permissions');
	}

	if (loading) {
		return (
			<UFOLoadHold name={UFOSegmentNames.ProjectAdminAccessCheckLoading}>
				<ProjectPickerLoadingSkeleton testId="projects-access-loading" />
			</UFOLoadHold>
		);
	}

	if (isOrgAdmin) {
		return (
			<EmptyState
				heading={formatMessage(messages.genericPermissionsStateHeading)}
				description={formatMessage(messages.turnOnProjectsDescription)}
				buttonLabel={formatMessage(messages.turnOnProjectsButtonLabel)}
				onClick={() => {
					if (orgId && cloudId) {
						window.open(getTownsquareUrl({ orgId, cloudId }), '_blank');
					}
				}}
			/>
		);
	}
	if (requestAccessSimplifiedUI) {
		return (
			<Box xcss={styles.container}>
				<Stack space="space.100">
					{formatMessage(messages.requestAccessDescription)}
					<Anchor href="#" onClick={onRequestAccess}>
						{formatMessage(messages.requestAccessLink)}
					</Anchor>
				</Stack>
			</Box>
		);
	}

	return (
		<EmptyState
			heading={formatMessage(messages.requestAccessHeading)}
			description={formatMessage(messages.requestAccessFullDescription)}
			buttonLabel={formatMessage(messages.requestAccessLink)}
			onClick={onRequestAccess}
		/>
	);
};
